import { X } from 'lucide-react';
import { Link } from 'react-router-dom';

const AboutUsModal = ({ isOpen, onClose }) => {

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[85vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gradient-to-r from-cyan-50 to-transparent">
          <div className="flex-grow text-center">
            <div className="flex items-center justify-center">
              {/* Logo Icon */}
              <div style={{
                width: '36px',
                height: '36px',
                position: 'relative',
                marginRight: '8px',
              }}>
                <svg viewBox="0 0 50 50" style={{
                  width: '100%',
                  height: '100%',
                  filter: 'drop-shadow(0 2px 4px rgba(0, 188, 212, 0.3))',
                }}>
                  <defs>
                    <linearGradient id="headerLogoGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" style={{ stopColor: '#00BCD4', stopOpacity: 1 }} />
                      <stop offset="50%" style={{ stopColor: '#0891B2', stopOpacity: 1 }} />
                      <stop offset="100%" style={{ stopColor: '#2563EB', stopOpacity: 1 }} />
                    </linearGradient>
                  </defs>
                  <g>
                    <path
                      fill="url(#headerLogoGradient)"
                      stroke="url(#headerLogoGradient)"
                      strokeWidth="1"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="
                      M 11.2,22.6
                      L 3.3,21.2
                      A 22 22 0 0 1 44.1,14.0
                      L 47.5,12.0
                      L 42.7,21.9
                      L 33.7,20.0
                      L 37.1,18.0
                      A 14 14 0 0 0 11.2,22.6
                      Z" />

                    <path
                      fill="url(#headerLogoGradient)"
                      stroke="url(#headerLogoGradient)"
                      strokeWidth="1"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="
                      M 38.8,27.4
                      L 46.7,28.8
                      A 22 22 0 0 1 6.0,36.0
                      L 2.5,38.0
                      L 7.3,28.1
                      L 16.3,30.0
                      L 12.9,32.0
                      A 14 14 0 0 0 38.8,27.4
                      Z" />
                  </g>
                </svg>
              </div>
              {/* Company Name */}
              <span style={{
                fontSize: '24px',
                fontWeight: '600',
                color: '#06B6D4',
                fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
              }}>rdinaa</span>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0 ml-4"
            aria-label="Close"
          >
            <X size={24} />
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="flex-grow overflow-y-auto bg-white">
          <div className="p-8 space-y-8 text-gray-800">
            {/* Hero Section */}
            <div className="text-center">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">From the Trenches to the Future</h3>
              <p className="text-base leading-relaxed text-black max-w-2xl mx-auto">
                Ordinaa wasn't built in a lab. It was born from a desire to <span className="font-bold text-teal-600">fix things that were broken</span>.
              </p>
            </div>

            {/* Our Origin */}
            <div className="text-center">
              <h3 className="text-xl font-bold text-gray-900 mb-4">We Lived the Problem Before We Wrote the Code</h3>
              <p className="leading-relaxed mb-3 max-w-2xl mx-auto text-base text-black">
                We didn't start with a pitch deck. We started with a problem we knew inside out.
              </p>
              <p className="leading-relaxed mb-3 max-w-2xl mx-auto text-base text-black">
                We began with <span className="font-bold text-cyan-600">LendSmart AI</span> because we had lived through the <span className="font-bold text-cyan-600">chaos of commercial lending</span>. We saw capable professionals drowning in paperwork and manual reviews. <span className="font-bold text-cyan-600">We knew that pain personally</span>, so we built a solution to fix it.
              </p>
              <p className="leading-relaxed mb-3 max-w-2xl mx-auto text-base text-black">
                But while building LendSmart, something shifted. Our primary goal was always to build a fortress around our users' data. <span className="font-bold text-cyan-600">We were obsessed with security</span>. That obsession led us down a rabbit hole into the world of <span className="font-bold text-cyan-600">Post-Quantum Cryptography (PQC)</span>.
              </p>
              <p className="leading-relaxed mb-3 max-w-2xl mx-auto text-base text-black">
                The more we read, the more we saw it: a "looming doom" for data security. As techies ourselves, we understood the gravity of what was coming. We realized that migrating to quantum-safe standards wasn't just a "nice to have"—it was a <span className="font-bold text-cyan-600">survival necessity</span> for developers everywhere.
              </p>
              <p className="leading-relaxed mb-3 max-w-2xl mx-auto text-base font-semibold text-black bg-cyan-100 py-2 px-3 rounded">
                We couldn't just sit back and watch. We had to act.
              </p>
              <p className="leading-relaxed max-w-2xl mx-auto text-base text-black">
                That realization gave birth to our hero product: <span className="font-bold text-cyan-600">The PQC Migration Tool</span>. And that is how Ordinaa truly came into existence—not just to automate workflows, but to <span className="font-bold text-cyan-600">secure the future</span> of the digital world.
              </p>
            </div>

            {/* Philosophy Section */}
            <div className="text-center">
              <h3 className="text-xl font-bold text-gray-900 mb-4">We Don't Chase Hype. <span className="text-purple-600 font-bold">We Solve Real Problems.</span></h3>
              <p className="leading-relaxed mb-3 max-w-2xl mx-auto text-base text-black">
                We are innovators, but we are practical first. We don't build technology for the sake of it. We build it to <span className="font-bold text-purple-600">remove the burden</span> from your shoulders.
              </p>
              <p className="leading-relaxed mb-3 max-w-2xl mx-auto text-base text-black">
                Our philosophy is simple. In a world full of over-promising and under-delivering, we stand by one rule above all else:
              </p>
              <p className="font-bold text-white mb-3 text-base bg-purple-600 py-3 px-4 rounded inline-block">
                We deliver what we promise.
              </p>
              <p className="leading-relaxed max-w-2xl mx-auto text-base text-black">
                Whether it is streamlining a complex audit or securing your code against a quantum threat, if we say we can do it, we do it.
              </p>
            </div>

            {/* Core Pillars Grid */}
            <div className="text-center">
              <h3 className="text-xl font-bold text-gray-900 mb-6">Our Foundation</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-2xl mx-auto">
                <div className="p-4 bg-cyan-50 rounded-lg border-l-4 border-cyan-500 text-center">
                  <p className="font-bold text-cyan-600 text-base mb-3">Practical, Not Theoretical</p>
                  <p className="text-sm text-black">We build for the reality of tight deadlines and regulatory scrutiny. If it doesn't solve a real operational pain, we don't build it.</p>
                </div>
                <div className="p-4 bg-purple-50 rounded-lg border-l-4 border-purple-500 text-center">
                  <p className="font-bold text-purple-600 text-base mb-3">Trust & Auditability</p>
                  <p className="text-sm text-black">In regulated industries, "black box" AI is a liability. Our systems are 100% traceable. Zero hallucinations and unbiased decision-making are our baselines.</p>
                </div>
                <div className="p-4 bg-teal-50 rounded-lg border-l-4 border-teal-500 text-center">
                  <p className="font-bold text-teal-600 text-base mb-3">Future-Proof Security</p>
                  <p className="text-sm text-black">The quantum era is approaching, and your digital security is at risk. We don't bolt security on later; from compliance to Post-Quantum Cryptography, protection is woven into our code.</p>
                </div>
                <div className="p-4 bg-blue-50 rounded-lg border-l-4 border-blue-500 text-center">
                  <p className="font-bold text-blue-600 text-base mb-3">Built in India, for the World</p>
                  <p className="text-sm text-black">Engineered with the scale and rigor of India's demanding enterprise ecosystem, designed to meet the highest global standards.</p>
                </div>
              </div>
            </div>

            {/* Why Ordinaa Section */}
            <div className="text-center bg-gradient-to-r from-cyan-50 via-purple-50 to-teal-50 p-8 rounded-lg border border-cyan-200">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Why Ordinaa?</h3>
              <p className="text-base leading-relaxed mb-4 max-w-2xl mx-auto text-black">
                Because critical decisions still depend on documents. Because workflows break at scale. Because you shouldn't have to choose between <span className="font-bold text-teal-600">innovation and security</span>.
              </p>
              <p className="text-base font-bold">
                <span className="text-cyan-600">Ordinaa:</span> <span className="text-gray-700">Work Smarter. Operate Faster. Stay Secure.</span>
              </p>
            </div>

            {/* Be Part of Our Journey Section */}
            <div className="text-center bg-gradient-to-r from-purple-50 via-cyan-50 to-teal-50 rounded-lg p-8 border border-purple-200">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Be Part of Our Journey.</h3>
              <p className="text-base leading-relaxed mb-4 max-w-2xl mx-auto text-black">
                We are building the future of <span className="font-bold text-teal-600">secure, intelligent enterprise</span>. Whether you are looking for a <span className="font-bold text-teal-600">solution, a partner</span>, or just a <span className="font-bold text-teal-600">conversation about where technology is heading</span>, we'd love to have you with us.
              </p>
              <p className="text-base leading-relaxed max-w-2xl mx-auto text-black">
                <span className="font-bold text-teal-600">Join us in any capacity you'd like</span>—let's <span className="font-bold text-teal-600">build something lasting together</span>.
              </p>
            </div>
          </div>
        </div>

        {/* CTA Button */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
          <Link
            to="/contact"
            onClick={onClose}
            className="w-full px-4 py-2 bg-gradient-to-r from-cyan-600 to-blue-600 text-white rounded-lg font-semibold hover:shadow-lg transition-all text-center block text-sm"
          >
            Let's Work Together
          </Link>
        </div>
      </div>
    </div>
  );
};

export default AboutUsModal;
